import React from 'react';
import { 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface RiskEventsTableProps {
  events: any[];
  onRefresh?: () => void;
}

const RiskEventsTable: React.FC<RiskEventsTableProps> = ({
  events
}) => {
  const getSeverityColor = (severity: string) => {
    switch (severity?.toUpperCase()) {
      case 'INFO':
        return 'text-info-custom bg-info-subtle';
      case 'WARNING':
        return 'text-warning-custom bg-warning-subtle';
      case 'CRITICAL':
        return 'text-error-custom bg-error-subtle';
      case 'EMERGENCY':
        return 'text-error-custom bg-error-subtle';
      default:
        return 'text-secondary-custom bg-secondary-custom';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity?.toUpperCase()) {
      case 'INFO':
        return <InformationCircleIcon className="h-4 w-4" />;
      case 'WARNING':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'CRITICAL':
      case 'EMERGENCY':
        return <XMarkIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getEventTypeLabel = (eventType: string) => {
    const typeMap: { [key: string]: string } = {
      'position_limit': 'Position Limit',
      'daily_loss': 'Daily Loss',
      'max_drawdown': 'Max Drawdown',
      'correlation_breach': 'Correlation Breach',
      'var_breach': 'VaR Breach',
      'exposure_limit': 'Exposure Limit',
      'volume_limit': 'Volume Limit',
      'trade_limit': 'Trade Limit',
      'stop_loss_triggered': 'Stop Loss',
      'emergency_stop': 'Emergency Stop',
      'system_halt': 'System Halt',
      'manual_intervention': 'Manual Intervention'
    };
    return typeMap[eventType] || eventType;
  };

  if (events.length === 0) {
    return (
      <div className="card shadow rounded-lg">
        <div className="px-6 py-4 border-b border-primary-custom">
          <h3 className="text-heading-3 font-medium text-primary-custom">Recent Risk Events</h3>
        </div>
        <div className="px-6 py-8 text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
          <h3 className="mt-2 text-body font-medium text-primary-custom">No Risk Events</h3>
          <p className="mt-1 text-body text-secondary-custom">
            No recent risk violations or events detected
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow rounded-lg">
      <div className="px-6 py-4 border-b border-primary-custom">
        <div className="flex justify-between items-center">
          <h3 className="text-heading-3 font-medium text-primary-custom">Recent Risk Events</h3>
          <span className="text-body text-secondary-custom">
            {events.length} unresolved event{events.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-primary-custom">
            <thead className="bg-secondary-custom sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-caption font-medium text-secondary-custom uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-primary-custom">
              {events.map((event, index) => (
                <tr key={event.id || index} className="hover-bg-secondary-custom">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-body font-medium text-primary-custom">
                        {getEventTypeLabel(event.event_type)}
                      </div>
                      <div className="text-body text-secondary-custom mt-1">
                        {event.event_message}
                      </div>
                      {event.trigger_value && event.limit_value && (
                        <div className="text-caption text-tertiary-custom mt-1">
                          Trigger: {event.trigger_value} / Limit: {event.limit_value}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption font-medium ${getSeverityColor(event.event_severity)}`}>
                      {getSeverityIcon(event.event_severity)}
                      <span className="ml-1">{event.event_severity}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-body text-secondary-custom">
                    {formatDateTime(event.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-body text-secondary-custom">
                    {event.action_taken ? (
                      <div>
                        <span className={`text-caption px-2 py-1 rounded ${
                          event.action_successful 
                            ? 'bg-success-subtle text-success-custom' 
                            : 'bg-error-subtle text-error-custom'
                        }`}>
                          {event.action_taken}
                        </span>
                        {!event.action_successful && (
                          <div className="text-caption text-red-500 mt-1">Failed</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-tertiary-custom">Alert only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {events.length > 5 && (
        <div className="px-6 py-3 bg-secondary-custom border-t border-primary-custom">
          <div className="text-body text-center text-secondary-custom">
            Showing {events.length} unresolved events
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskEventsTable; 